import React from 'react';

const ReviewSchema = ({ 
  businessName, 
  trustScore, 
  reviewCount, 
  reviews, 
  logoUrl,
  category,
  phone,
  address, 
  city,
  countryCode,
  postalCode,
  domain // Pass the client's domain (e.g., 'alessiowagner.com') as a prop
}) => {
  // --- 1. PRE-FLIGHT & IDENTITY LOGIC ---
  const isClientSite = typeof window !== 'undefined' && !window.location.hostname.includes('sitemanifest.com');
  const currentUrl = typeof window !== 'undefined' ? window.location.href.split('?')[0] : '';
  
  // The "Bridge": Link the current page to the Sitemanifest Authority Profile
  const manifestProfileUrl = `https://sitemanifest.com/company/${domain || 'general'}`;
  
  if (!trustScore || !reviews || !Array.isArray(reviews) || reviews.length === 0) {
    return null;
  }

  // --- 2. DATA MAPPING ---
  const typeMap = {
    'car_dealer': 'AutoDealer',
    'luxury_cars': 'AutoDealer',
    'Jewelry Store': 'JewelryStore',
    'watches': 'JewelryStore',
    'travel': 'TravelAgency',
    'real_estate': 'RealEstateAgent',
    'art': 'ArtGallery',
    'doctor': 'MedicalBusiness'
  };

  const countryToCode = {
    'united states': 'US', 'canada': 'CA', 'mexico': 'MX', 'georgia': 'GE', 
    'united kingdom': 'GB', 'uk': 'GB', 'france': 'FR', 'germany': 'DE', 
    'italy': 'IT', 'spain': 'ES', 'monaco': 'MC', 'switzerland': 'CH',
    'united arab emirates': 'AE', 'uae': 'AE', 'qatar': 'QA'
  };

  const businessType = typeMap[category] || 'LocalBusiness';
  const finalCode = countryToCode[countryCode?.toLowerCase().trim()] || countryCode?.toUpperCase() || "US";

  // --- 3. THE "AUTHORITY" SCHEMA CONSTRUCTION ---
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": businessType,
    // If on client site, the ID is their domain. If on Sitemanifest, it's our profile URL.
    "@id": isClientSite ? `${window.location.origin}/#business` : `${manifestProfileUrl}#business`,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": currentUrl 
    },
    "name": businessName,
    "url": isClientSite ? window.location.origin : manifestProfileUrl,
    "image": logoUrl || "",
    "telephone": phone || "",
    "sameAs": isClientSite 
  ? [manifestProfileUrl] 
  : (domain ? [domain.startsWith('http') ? domain : `https://${domain}`] : []),

    "address": {
      "@type": "PostalAddress",
      "streetAddress": address || "Contact for Address",
      "addressLocality": city || "Global",
      "postalCode": postalCode || "",
      "addressCountry": finalCode
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": parseFloat(trustScore).toFixed(1), // Numeric for better parsing
      "reviewCount": parseInt(reviewCount || reviews.length),
      "bestRating": 5,
      "worstRating": 1
    },
    "review": reviews
      .filter(rev => rev.is_approved === true)
      .slice(0, 20) // Limit to top 20 for page speed/DOM size
      .map((rev) => ({
        "@type": "Review",
        "author": { 
          "@type": "Person", 
          "name": rev.author || "Customer" 
        },
        "publisher": {
          "@type": "Organization",
          "name": "Sitemanifest",
          "url": "https://sitemanifest.com"
        },
        "datePublished": new Date(rev.date || rev.created_at || Date.now()).toISOString().split('T')[0], 
        "reviewBody": (rev.text || "").replace(/\n/g, " ").trim(),
        "name": rev.title ? `Verified Experience: ${rev.title}` : "Verified Review via Sitemanifest",
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": parseFloat(rev.stars || 5),
          "bestRating": 5,
          "worstRating": 1
        }
      }))
  };

  return (
    <script
      type="application/ld+json"
      key={`sm-schema-${domain}-${businessName}`}
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
    />
  );
};

export default ReviewSchema;